/**
 * Selected app instance from Zustand (synced to localStorage dc.app_id for cpFetch).
 */

import { useAppStore } from '@/store/appStore';

/** Platform ops app — Dev Cockpit Core; sees all templates (core catalog). */
export const DEVCOCKPIT_CORE_APP_ID = 'app_5UxnQWMZZ2Fa';

export const CORE_APP_IDS = new Set([
  'control-plane',
  'devcockpit-core',
  'farmer-portal',
  DEVCOCKPIT_CORE_APP_ID,
  '',
]);

const CORE_APP_NAME_PATTERNS = [/dev\s*cockpit\s*core/i];

/** Raw app id from store/localStorage (includes core platform apps). */
export function useActiveAppId(): string | null {
  const storeAppId = useAppStore((s) => s.appId);
  if (storeAppId) return storeAppId;
  if (typeof window !== 'undefined') {
    try {
      const ls = localStorage.getItem('dc.app_id');
      if (ls) return ls;
    } catch {
      // ignore
    }
  }
  return null;
}

export function useSelectedAppId(): string | null {
  const appId = useActiveAppId();
  const appName = useSelectedAppName();
  if (appId && !isCoreAppId(appId, appName)) return appId;
  return null;
}

export function useSelectedAppName(): string | null {
  return useAppStore((s) => s.appName);
}

export function isCoreAppId(appId: string | null | undefined, appName?: string | null): boolean {
  const id = String(appId || '').trim();
  if (!id || CORE_APP_IDS.has(id)) return true;
  const name = String(appName || '').trim();
  if (name && CORE_APP_NAME_PATTERNS.some((pattern) => pattern.test(name))) return true;
  return false;
}

/** Append app_id + catalogScope for template catalog endpoints. */
export function appendTemplateScopeParams(
  params: URLSearchParams,
  appId: string | null,
  catalogScope?: string,
  appName?: string | null
) {
  if (appId && !isCoreAppId(appId, appName)) {
    params.set('app_id', appId);
    params.set('catalogScope', catalogScope || 'app');
  } else if (catalogScope) {
    params.set('catalogScope', catalogScope);
  }
}

/** Append app_id query param when backend reads req.query.app_id (e.g. relationships graph). */
export function appendAppIdQueryParam(
  params: URLSearchParams,
  appId: string | null,
  appName?: string | null
) {
  if (appId && !isCoreAppId(appId, appName)) {
    params.set('app_id', appId);
  }
}
